import { Controller } from "@hotwired/stimulus"
import mapboxManager from "../../components/mapbox_manager"

// Job form location field backed by a Mapbox geocoder.
export default class extends Controller {
  static targets = ["location", "lat", "lng", "geocoderMount", "locationWrapper"]
  static values = {
    accessToken: String,
    placeholder: String,
  }

  connect() {
    this.abortController = new AbortController()
    this.initialize()
  }

  disconnect() {
    this.abortController.abort()
    this.teardownMapboxGeocoder()
  }

  initialize() {
    this.initializeMapboxGeocoder()
  }

  async initializeMapboxGeocoder() {
    if (this.geocoder) return

    const mount = this.hasGeocoderMountTarget ? this.geocoderMountTarget : this.element
    mount.querySelector(".mapbox-geocoder-container")?.remove()

    if (!this.accessTokenValue) {
      console.error("Mapbox access token not provided")
      return
    }

    await mapboxManager.loadAPI(this.accessTokenValue)
    if (this.abortController.signal.aborted) return

    const dummyMap = {
      getCenter: () => ({ lng: 0, lat: 0, wrap: () => ({ lng: 0, lat: 0 }) }),
      getZoom: () => 10,
      flyTo() {},
      on() {
        return this
      },
      off() {
        return this
      },
      getBounds: () => ({
        extend: () => {},
        getNorth: () => 0,
        getSouth: () => 0,
        getEast: () => 0,
        getWest: () => 0,
      }),
      loaded: () => true,
      fire: () => {},
      _container: document.createElement("div"),
    }

    const geocoder = await mapboxManager.createGeocoder({
      accessToken: this.accessTokenValue,
      mapboxgl: window.mapboxgl,
      placeholder: this.placeholderValue || this.locationTarget.placeholder || "Search for an address",
      countries: "us",
      types: "address,place,region",
      marker: false,
      flyTo: false,
    })

    if (this.abortController.signal.aborted) return

    const geocoderContainer = geocoder.onAdd(dummyMap)
    geocoderContainer.className = `${geocoderContainer.className || ""} mapbox-geocoder-container`
    if (this.hasGeocoderMountTarget) {
      this.geocoderMountTarget.replaceChildren(geocoderContainer)
    } else {
      this.locationTarget.parentNode.insertBefore(geocoderContainer, this.locationTarget.nextSibling)
    }
    this.hideLocationInput()
    this.geocoderContainer = geocoderContainer
    this.geocoder = geocoder

    geocoder.on("result", (event) => {
      const result = event.result
      if (result.place_name) {
        this.locationTarget.value = result.place_name
      } else if (result.text) {
        const addressParts = [result.text]
        result.context?.forEach((item) => {
          if (["place", "district", "locality", "region", "postcode"].includes(item.id.split(".")[0])) {
            addressParts.push(item.text)
          }
        })
        this.locationTarget.value = addressParts.join(", ")
      }

      if (result.center?.length === 2) {
        this.lngTarget.value = result.center[0]
        this.latTarget.value = result.center[1]
      }

      this.locationTarget.dispatchEvent(new Event("change", { bubbles: true }))
      this.latTarget.dispatchEvent(new Event("change", { bubbles: true }))
      this.lngTarget.dispatchEvent(new Event("change", { bubbles: true }))
    })

    geocoder.on("error", (event) => {
      console.error("Geocoder error:", event.error)
    })

    geocoder.on("clear", () => {
      this.locationTarget.value = ""
      this.latTarget.value = ""
      this.lngTarget.value = ""
    })

    if (this.locationTarget.value) {
      geocoder.setInput(this.locationTarget.value)
    }
  }

  teardownMapboxGeocoder() {
    if (this.geocoderContainer) {
      this.geocoderContainer.remove()
      this.geocoderContainer = null
    }
    if (this.hasGeocoderMountTarget) {
      this.geocoderMountTarget.replaceChildren()
    }
    this.geocoder = null
    this.showLocationInput()
  }

  hideLocationInput() {
    if (this.hasLocationWrapperTarget) {
      this.locationWrapperTarget.style.display = "none"
      return
    }
    if (this.hasLocationTarget) {
      this.locationTarget.style.display = "none"
    }
  }

  showLocationInput() {
    if (this.hasLocationWrapperTarget) {
      this.locationWrapperTarget.style.display = ""
      return
    }
    if (this.hasLocationTarget) {
      this.locationTarget.style.display = ""
    }
  }
}
